import App from './App.vue';
import Vue from 'vue';
import VueRouter from 'vue-router';
import QueryString from 'designmynight-javascript-sdk/src/Http/QueryString.js';

import Widget from './models/Widget';
import TagOptions from './models/TagOptions';

// Components
import EventsList from './components/events-list/EventsList.vue';
import CalendarBase from './components/calendar/calendar-base/CalendarBase.vue';
import TicketsBase from './components/tickets/ticket-base/TicketsBase.vue';
import Checkout from './components/checkout/Checkout.vue';

import STAGES from './constants/StagesConstant';

export default class TicketWidgetHelper {
    widgets: Widget[] = []

    /**
     * Finds script tags on the page. Goes through each one, ensures it has the
     * required attributes, and that it hasn't already been rendered. Then
     * renders it.
     * @return {Widget[]}
     */
    public findTicketWidgetScriptTags (): Widget[] {
        const scriptTags = document.getElementsByTagName('script');

        for (let i = 0; i < scriptTags.length; i++) {
            this.renderWidget(scriptTags[i]);
        }
        
        return this.widgets;
    }

    /**
     * Renders the specified HTMLScriptElement tag as a Booking Form Widget
     * and adds it to the array of widgets on this page
     * @param {HTMLScriptElement} tag
     * @return {void}
     */
    public renderWidget (tag: HTMLScriptElement): Widget | null {
        if (this.isValidTag(tag) && !this.isTagRendered(tag)) {
            const options = this.extractPropertiesFromTag(tag);
            const vue = this.bootVueForTag(tag, options);
            const widget = new Widget(vue);

            this.setTagAsRendered(tag)
                .addWidgetToArray(widget);
            
            return widget;
        }

        return null;
    }

    /**
     * Returns a widget by the reference specified as the ref attribute
     * on the script tag, or null if it was not found.
     * @param {string} ref
     * @return {Widget | null}
     */
    public getWidgetByRef (ref: string): Widget | null {
        let widgetToReturn: any;

        this.widgets.forEach((widget) => {
            if (widget.options.ref === ref) {
                widgetToReturn = widget;
                return;
            }
        })

        return widgetToReturn;
    }

    /**
     * Determines whether or not the specified tag is a valid booking form
     * script tag by checking if it has all the required attributes
     * @param {HTMLScriptElement} tag
     * @return {boolean}
     */
    public isValidTag (tag: HTMLScriptElement): boolean {
        let valid = true;

        TagOptions.requiredFields.forEach((field) => {
            if (!tag.hasAttribute(field)) {
                valid = false;
            }
        })
        valid = this.isAllowedSource(tag);

        const parent: any = tag.parentElement;

        if(parent.nodeName === 'HEAD' || parent.nodeName === 'HTML') {
            valid = false;
        }

        return valid;
    }

    /**
     * Determines whether or not the specified tag has a trusted source
     * @param {HTMLScriptElement} tag
     * @return {boolean}
     */
    private isAllowedSource (tag: HTMLScriptElement): boolean {
        let valid: boolean = false;

        const allowedSources: RegExp[] = [
            /app.js/,
            /ticket-widget.qa.min.js/,
            /ticket-widget.min.js/
        ];

        const src = tag.getAttribute('src');

        allowedSources.forEach((regex) => {
            if (regex.test(src!)) {
                valid = true;
            }
        })

        return valid;
    }

    /**
     * Returns whether or not the specified script tag has been rendered already
     * @param {HTMLScriptElement} tag
     * @return {boolean}
     */
    public isTagRendered (tag: HTMLScriptElement): boolean {
        return tag.getAttribute('data-rendered') === 'true';
    }

    /**
     * Extracts the properties of the script tag and returns a TagOptions object
     * @param {HTMLScriptElement} tag
     * @return {TagOptions}
     */
    private extractPropertiesFromTag (tag: HTMLScriptElement): TagOptions {
        const partnerUrl = tag.getAttribute('src');
        const queryString = QueryString.fromString(partnerUrl);

        let tagOptions = new TagOptions(queryString.params);

        return tagOptions;
    }

    /**
     * Internally sets the specified script tag as already rendered, so that it
     * won't get rendered again
     * @param {HTMLScriptElement} tag
     * @return {this}
     */
    private setTagAsRendered (tag: HTMLScriptElement): this {
        tag.setAttribute('data-rendered', 'true');

        return this;
    }

    /**
     * Instantiates a new instance of VueJS for the specified script tag,
     * passing in all the specified options
     * @param {HTMLScriptElement} tag
     * @return {Vue}
     */
    private bootVueForTag(tag: HTMLScriptElement, options: TagOptions): Vue {

        const router =  new VueRouter({
            mode: 'abstract',
            routes: [
                {
                    name: 'initial',
                    path: '/',
                    component: App,
                    meta: { 
                        scrollToTop: false,
                        stage: STAGES.initial
                    }
                },
                {
                    name: 'events-list',
                    path: '/events-list',
                    component: EventsList,
                    meta: { 
                        scrollToTop: false,
                        stage: STAGES.events_list
                    }
                },
                {
                    name: 'calendar',
                    path: '/calendar',
                    component: CalendarBase,
                    meta: { 
                        scrollToTop: false,
                        stage: STAGES.calendar
                    }
                },
                {
                    name: 'tickets',
                    path: '/tickets',
                    component: TicketsBase,
                    meta: { 
                        scrollToTop: false,
                        stage: STAGES.tickets
                    }
                },
                {
                    name: 'checkout',
                    path: '/checkout',
                    component: Checkout,
                    meta: { 
                        scrollToTop: false,
                        stage: STAGES.checkout
                    }
                }
            ]
        });

        return new Vue({
            el: tag,
            router: router,
            render: boot => boot(App),
            data: {
                dmnOptions: options,
                stages: STAGES,
                config: {
                    currentStage: STAGES.initial
                }
            },
        });
    }

    private addWidgetToArray (widget: Widget): this {
        this.widgets.push(widget);

        return this;
    }
}